/**
 * Realtime client for the Quickback broadcast service.
 *
 * Flow:
 *   1. POST /broadcast/v1/ws-ticket (cookie auth) → { wsTicket, expiresIn }
 *   2. WebSocket wss://<api>/broadcast/v1/websocket?ws_ticket=<ticket>
 *   3. Server stamps the connection to the user's organization (from the
 *      ticket). All inbound messages are already scoped — no client filter.
 *   4. Reconnect with exponential backoff on close; pause when the tab is
 *      hidden, resume + invalidate-once when visible again.
 *
 * Mounted by `<RealtimeBridge />` (renders null). Inbox events patch the
 * shared QueryClient cache in place — never a list invalidation. Per-user
 * mailbox state (read/archive/star/labels/drafts) arrives only on this
 * user's sessions; messages and meeting invites are org-wide.
 */
import { useEffect, useRef } from "react";
import { useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { App as CapacitorApp } from "@capacitor/app";
import type { PluginListenerHandle } from "@capacitor/core";
import { messages as messagesApi, type MailboxThreadsResponse, type MailLabel, type Message, type Paged } from "./api";
import {
    patchMailboxLabel,
    patchMailboxThreadLabels,
    patchMailboxThreadState,
    removeMailboxMessage,
    replaceMailboxMessage,
    setMailboxUnreadTotal,
    upsertMailboxMessages,
    type MailboxStateOperation,
} from "./mailbox-cache";
import { patchInviteCaches, type InviteUpdate } from "./calendar-invite";
import { addNotification, patchNotificationState } from "./notifications-cache";
import type { AppNotification } from "./api";
import { authenticatedFetch, isNativeIOS } from "./native-auth";
import { CASES_QUERY_KEY, CHAT_QUERY_KEY, MAILBOX_SUMMARY_QUERY_KEY, MAILBOX_THREADS_QUERY_KEY, MESSAGES_QUERY_KEY, NOTIFICATIONS_QUERY_KEY } from "./query-keys";

const API_BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";
const WS_BASE: string = API_BASE
    ? API_BASE.replace(/^http/, "ws")
    : `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}`;

interface TicketResponse {
    wsTicket:  string;
    expiresIn: number;
}

interface BroadcastEnvelope {
    // CloudEvents 1.0 envelope (current workers): discriminate on `qbframe`,
    // event wire-name in `type`, payload in `data`.
    qbframe?: string;
    data?:    Record<string, unknown>;
    // Legacy raw frame (pre-CloudEvents workers) — kept so the tab survives
    // the deploy boundary in either direction.
    type:    "broadcast" | "postgres_changes" | string;
    event?:  string;
    payload?: Record<string, unknown>;
}

const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 15_000, 30_000];

async function fetchTicket(): Promise<TicketResponse> {
    const res = await authenticatedFetch(`${API_BASE}/broadcast/v1/ws-ticket`, {
        method:      "POST",
        headers:     { "Content-Type": "application/json" },
        body:        "{}",
    });
    if (!res.ok) throw new Error(`ws-ticket ${res.status}`);
    return res.json() as Promise<TicketResponse>;
}

export function useRealtime(): void {
    const queryClient = useQueryClient();

    // Refs survive renders; the closure inside useEffect captures them.
    const wsRef         = useRef<WebSocket | null>(null);
    const reconnectRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
    const attemptRef    = useRef(0);
    const closedByMeRef = useRef(false);
    const generationRef = useRef(0);

    useEffect(() => {
        let appStateHandle: PluginListenerHandle | null = null;
        let disposed = false;

        function invalidateWorkspace() {
            const keys: ReadonlyArray<readonly unknown[]> = [
                MESSAGES_QUERY_KEY,
                MAILBOX_THREADS_QUERY_KEY,
                MAILBOX_SUMMARY_QUERY_KEY,
                NOTIFICATIONS_QUERY_KEY,
                CHAT_QUERY_KEY,
                CASES_QUERY_KEY,
                ["meetings"],
                ["bookings"],
                ["events"],
                ["calendar-feed-busy"],
                ["records"],
                ["contacts"],
            ];
            for (const key of keys) void queryClient.invalidateQueries({ queryKey: key });
        }

        function clearReconnect() {
            if (reconnectRef.current) {
                clearTimeout(reconnectRef.current);
                reconnectRef.current = null;
            }
        }

        function disconnect() {
            closedByMeRef.current = true;
            generationRef.current += 1;
            clearReconnect();
            const socket = wsRef.current;
            wsRef.current = null;
            if (socket) {
                try { socket.close(1000, "client closing"); } catch { /* ignore */ }
            }
        }

        /** Swap a partial realtime row for the full one (one-row GET, no list refetch). */
        async function hydrateMessage(id: string): Promise<void> {
            try {
                const full = await messagesApi.get(id);
                if (full?.id) replaceMailboxMessage(queryClient, id, full);
            } catch { /* the partial row still renders from bodyText */ }
        }

        function handleEnvelope(env: BroadcastEnvelope): void {
            // Normalize both framings to (event, payload).
            let event: string | undefined;
            let payload: Record<string, unknown> | undefined;
            if (env.qbframe === "broadcast") {
                event = env.type;
                payload = env.data;
            } else if (env.type === "broadcast") {
                event = env.event;
                payload = env.payload;
            }
            if (!event || !payload) return;

            if (event === "messages:created") {
                const incoming = payload as unknown as Message & { partial?: boolean };
                if (incoming.status === "draft") return; // drafts travel on drafts:changed
                // Projects into the open thread, every loaded mailbox view and
                // the unread count — no list refetch.
                upsertMailboxMessages(queryClient, [incoming]);
                // Broadcasts omit bodyHtml (size); fetch just this one row.
                if (incoming.partial) void hydrateMessage(incoming.id);
                return;
            }

            if (event === "messages:updated") {
                const patch = payload as Partial<Message> & { id: string };
                queryClient.setQueryData<Message[]>(MESSAGES_QUERY_KEY, (prev = []) =>
                    prev.map((m) => (m.id === patch.id ? { ...m, ...patch } : m)));
                queryClient.setQueriesData<InfiniteData<Paged<Message>>>(
                    { queryKey: [...MESSAGES_QUERY_KEY, "thread"] },
                    current => current ? {
                        ...current,
                        pages: current.pages.map(page => ({
                            ...page,
                            data: page.data.map(message => message.id === patch.id ? { ...message, ...patch } : message),
                        })),
                    } : current,
                );
                queryClient.setQueriesData<InfiniteData<MailboxThreadsResponse>>(
                    { queryKey: MAILBOX_THREADS_QUERY_KEY },
                    current => current ? {
                        ...current,
                        pages: current.pages.map(page => ({
                            ...page,
                            threads: page.threads.map(thread => thread.latestMessage.id === patch.id
                                ? { ...thread, latestMessage: { ...thread.latestMessage, ...patch } }
                                : thread),
                        })),
                    } : current,
                );
                return;
            }

            // ── Per-user mailbox state (sent only to this user's sessions) ──
            if (event === "mailbox:thread-state") {
                const { operation, threadKeys, inboxUnread } = payload as { operation: MailboxStateOperation; threadKeys: string[]; inboxUnread?: number };
                patchMailboxThreadState(queryClient, operation, threadKeys);
                if (typeof inboxUnread === "number") setMailboxUnreadTotal(queryClient, inboxUnread);
                return;
            }
            if (event === "mailbox:thread-labels") {
                const { operation, threadKeys, labelIds } = payload as { operation: "add" | "remove" | "replace"; threadKeys: string[]; labelIds: string[] };
                patchMailboxThreadLabels(queryClient, operation, threadKeys, labelIds);
                return;
            }
            if (event === "mailbox:labels") {
                patchMailboxLabel(queryClient, payload as { label: MailLabel } | { deletedId: string });
                return;
            }
            if (event === "drafts:changed") {
                const change = payload as { draft?: Message } | { deletedId?: string };
                if ("deletedId" in change && change.deletedId) {
                    removeMailboxMessage(queryClient, change.deletedId);
                } else if ("draft" in change && change.draft) {
                    const draft = change.draft;
                    queryClient.setQueryData<Message[]>(MESSAGES_QUERY_KEY, (prev = []) => {
                        const current = prev.find(message => message.id === draft.id);
                        // Keep a body we already have; the payload omits bodyHtml.
                        const merged = current ? { ...current, ...draft, bodyHtml: current.bodyHtml } : draft;
                        return [merged, ...prev.filter(message => message.id !== draft.id)];
                    });
                    void hydrateMessage(draft.id);
                }
                return;
            }
            if (event === "calendar-invite:updated") {
                patchInviteCaches(queryClient, payload as unknown as InviteUpdate);
                return;
            }

            if (event === "chat:changed") {
                void queryClient.invalidateQueries({ queryKey: CHAT_QUERY_KEY });
                const conversationId = typeof payload.conversationId === "string" ? payload.conversationId : null;
                if (conversationId) {
                    void queryClient.invalidateQueries({ queryKey: ["kardoe-chat", "messages", conversationId] });
                }
                return;
            }

            if (event === "cases:changed") {
                void queryClient.invalidateQueries({ queryKey: CASES_QUERY_KEY });
                const caseId = typeof payload.caseId === "string" ? payload.caseId : null;
                if (caseId) {
                    void queryClient.invalidateQueries({ queryKey: ["kardoe-cases", "detail", caseId] });
                }
                return;
            }
            if (event === "notifications:created") {
                addNotification(queryClient, (payload as { notification: AppNotification }).notification);
                return;
            }
            if (event === "notifications:state") {
                const change = payload as { ids: string[] | null; all: boolean; dismissed: boolean; at: string };
                patchNotificationState(queryClient, { ids: change.all ? null : change.ids, dismissed: change.dismissed, at: change.at });
                return;
            }
            // Legacy coarse ping (chat still sends it): refresh just the notification list.
            if (event === "notifications:changed") {
                void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
                return;
            }
            if (event === "calendar:changed") {
                for (const key of [["meetings"], ["bookings"], ["events"], ["calendar-feed-busy"]] as const) {
                    void queryClient.invalidateQueries({ queryKey: key });
                }
                return;
            }
            if (event === "records:changed") {
                void queryClient.invalidateQueries({ queryKey: ["records"] });
                return;
            }
            // Unknown event — silently ignore. We don't want a new server-side
            // broadcast to crash the inbox tab.
        }

        async function connect(): Promise<void> {
            closedByMeRef.current = false;
            const generation = ++generationRef.current;
            try {
                const { wsTicket } = await fetchTicket();
                // The app may have backgrounded while the ticket request was
                // in flight. Do not create a zombie socket after disconnect.
                if (generation !== generationRef.current) return;
                const ws = new WebSocket(`${WS_BASE}/broadcast/v1/websocket?ws_ticket=${encodeURIComponent(wsTicket)}`);
                wsRef.current = ws;

                ws.onopen = () => {
                    attemptRef.current = 0;
                };

                ws.onmessage = (ev: MessageEvent<string>) => {
                    let env: BroadcastEnvelope;
                    try { env = JSON.parse(ev.data) as BroadcastEnvelope; }
                    catch { return; }
                    handleEnvelope(env);
                };

                ws.onerror = () => {
                    // onclose will fire too — let it own the backoff schedule.
                };

                ws.onclose = () => {
                    if (generation !== generationRef.current) return;
                    wsRef.current = null;
                    if (closedByMeRef.current) return;
                    const delay = BACKOFF_MS[Math.min(attemptRef.current, BACKOFF_MS.length - 1)];
                    attemptRef.current += 1;
                    clearReconnect();
                    reconnectRef.current = setTimeout(() => { void connect(); }, delay);
                };
            } catch (err) {
                if (generation !== generationRef.current || closedByMeRef.current) return;
                // Ticket fetch failed — likely not authenticated yet. Retry with backoff.
                const delay = BACKOFF_MS[Math.min(attemptRef.current, BACKOFF_MS.length - 1)];
                attemptRef.current += 1;
                clearReconnect();
                reconnectRef.current = setTimeout(() => { void connect(); }, delay);
            }
        }

        function onVisibilityChange(): void {
            if (document.visibilityState === "hidden") {
                disconnect();
            } else if (!wsRef.current) {
                attemptRef.current = 0;
                // Invalidate so we don't miss anything that landed while hidden
                // — the WS will catch up once it's open; the refetch fills the
                // gap immediately.
                invalidateWorkspace();
                void connect();
            }
        }

        if (isNativeIOS()) {
            void CapacitorApp.addListener("appStateChange", ({ isActive }) => {
                if (!isActive) {
                    disconnect();
                } else if (!wsRef.current) {
                    attemptRef.current = 0;
                    invalidateWorkspace();
                    void connect();
                }
            }).then(handle => {
                if (disposed) void handle.remove();
                else appStateHandle = handle;
            });
        } else {
            document.addEventListener("visibilitychange", onVisibilityChange);
        }
        // Kick off the initial connection.
        void connect();

        return () => {
            disposed = true;
            if (isNativeIOS()) {
                if (appStateHandle) void appStateHandle.remove();
            } else {
                document.removeEventListener("visibilitychange", onVisibilityChange);
            }
            disconnect();
        };
        // queryClient is stable across renders (provided by QueryClientProvider).
    }, [queryClient]);
}
